import { oids } from "@/configs/Settings";
import { fhirClients } from "src-next/shared/api/fhirClient";
import { extractResourcesFromBundle } from "../../statistics/helpers/crmMessageFranchiseStatsHelpers";

const PROFILE_URL =
  "http://fluxmed.cloud/fhir/StructureDefinition/WhatsAppTemplateMedia";
const CODE_SYSTEM = "http://fluxmed.cloud/fhir/CodeSystem/basic-resource-type";
const CODE = "WHATSAPP_TEMPLATE_MEDIA";
const TEMPLATE_NAME_IDENTIFIER_SYSTEM =
  "http://fluxmed.cloud/fhir/whatsapp-template-name";
const MEDIA_ID_EXTENSION_URL =
  "http://fluxmed.cloud/fhir/StructureDefinition/whatsapp-media-id";

function buildOrganizationReference(organizationId: string): string {
  const trimmed = organizationId.trim();
  const prefix = `${oids.cnpj}-`;
  if (trimmed.startsWith("Organization/")) return trimmed;
  if (trimmed.startsWith(prefix)) return `Organization/${trimmed}`;
  return `Organization/${prefix}${trimmed}`;
}

function buildBasic(params: {
  templateName: string;
  organizationReference: string;
  mediaId: string;
  existing?: fhir5.Basic;
}): fhir5.Basic {
  const extension = [
    ...(params.existing?.extension ?? []).filter(
      (item) => item.url !== MEDIA_ID_EXTENSION_URL,
    ),
    {
      url: MEDIA_ID_EXTENSION_URL,
      valueString: params.mediaId,
    },
  ];

  return {
    ...(params.existing?.id ? { id: params.existing.id } : {}),
    resourceType: "Basic",
    meta: { profile: [PROFILE_URL] },
    code: {
      coding: [{ system: CODE_SYSTEM, code: CODE }],
    },
    identifier: [
      {
        system: TEMPLATE_NAME_IDENTIFIER_SYSTEM,
        value: params.templateName,
      },
    ],
    author: { reference: params.organizationReference },
    created: params.existing?.created ?? new Date().toISOString(),
    extension,
  };
}

async function findExisting(params: {
  templateName: string;
  organizationReference: string;
}): Promise<fhir5.Basic | null> {
  const bundle = await fhirClients.admin.search<fhir5.Basic>("Basic", {
    identifier: `${TEMPLATE_NAME_IDENTIFIER_SYSTEM}|${params.templateName}`,
  });

  const match = extractResourcesFromBundle<fhir5.Basic>(bundle).find(
    (basic) =>
      basic.author?.reference === params.organizationReference &&
      basic.code?.coding?.some((coding) => coding.code === CODE),
  );

  return match ?? null;
}

export async function upsertWhatsAppTemplateMediaBasic(params: {
  organizationId: string;
  templateName: string;
  mediaId: string;
}): Promise<void> {
  const organizationId = params.organizationId.trim();
  const templateName = params.templateName.trim();
  const mediaId = params.mediaId.trim();

  if (!organizationId) {
    throw new Error("Organização é obrigatória para salvar o id de mídia.");
  }
  if (!templateName) {
    throw new Error("Nome do template é obrigatório para salvar o id de mídia.");
  }
  if (!mediaId) {
    throw new Error("Id de mídia é obrigatório.");
  }

  const organizationReference = buildOrganizationReference(organizationId);
  const existing = await findExisting({ templateName, organizationReference });
  const resource = buildBasic({
    templateName,
    organizationReference,
    mediaId,
    existing: existing ?? undefined,
  });

  if (existing?.id) {
    await fhirClients.admin.update<fhir5.Basic>("Basic", existing.id, resource);
    return;
  }

  await fhirClients.admin.create<fhir5.Basic>("Basic", resource);
}
